import React, { useState, useEffect, useContext } from 'react';
import '../view-archer-styles.css';

import { Box, FormControl, Grid, InputLabel, MenuItem, Paper, Stack, Select} from '@mui/material/';
import { styled,} from '@mui/material/styles';
import { Drafts as Draft, Email as Sent, AccountCircle } from '@mui/icons-material/';
import { orange, blue, red} from '@mui/material/colors';

//components import
import AuditorsDropdown from '../../../components/reference/AuditorsDropdown';
import SearchSection from '../../../components/nav/SearchSection';
import ArcherFiler from '../dataObjects/ArcherFiler';
import { FilerCommunicationContext } from './FilersRoot';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import { AuditorCommunicationContext } from '../view-archer';

//const data
import AuditorList from '../../../data/AuditorList';

interface Props {
    auditorId: number,
    searchInput: string,
    setDefaultCpfId: (cpfId: number) => void,
    updateAuditorId: (auditorId: number) => void
}

//mock data
const AUDITORS = AuditorList;

const LetterStatus = [
    {
        statusId: 1,
        description: "Draft"
    },
    {
        statusId: 2,
        description: "Pending Responses"
    },
    {
        statusId: 3,
        description: "Outstanding"
    },
    {
        statusId: 4,
        description: "Delinquent"
    },
    {
        statusId: 5,
        description: "All"
    }
]

const DefaultStatusId = 3;

const FilerListSideBar = (props: Props) => {

    //filer list block styles
    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        padding: '3px',
        textAlign: 'left',
        color: theme.palette.text.secondary,
        backgroundColor: '#F6FBFE',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        minHeight: 45,
        minWidth: '95%',
        margin: '5px',
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    }));

    const context = useContext(FilerCommunicationContext);
    const auditorContext = useContext(AuditorCommunicationContext);

    //filer list filter
    const [auditorId, setAuditorId] = useState(props.auditorId);
    const [selectedStatusId, setStatus] = useState(DefaultStatusId);
    const [searchInput, setSearchInput] = useState(props.searchInput);
    const [filerList, setFilerList] = useState<ArcherFiler[]>();

    //initial payload
    useEffect(() => {
        fetchFilerList()
    }, [auditorId, selectedStatusId, searchInput]);


    const displayFilerStatusIcon = (status: number) => {
        switch (status) {
            case 1:
                return (
                    <Draft fontSize="small" sx={{ color: '#ffc107' }} />
                );
            case 2:
                return (
                    <Sent fontSize="small" sx={{ color: orange[500]}} />
                );
            case 3:
                return (
                    <Sent fontSize="small" sx={{ color: red[200]}}/>
                );
            case 4:
                return (
                    <Sent fontSize="small" sx={{ color: red[700]}} />
                );
            case 5:
                return (
                    <AccountCircle fontSize="small" sx={{ color: blue[500]}} />
                );
            default:
                return '';
        }
    }

    const fetchFilerList = async () => {

        if (searchInput.length > 0) {
            setAuditorId(0);
            setStatus(5); //all
        };

        var params = [
            `auditorId=${auditorId}`,
            `statusId=${selectedStatusId}`,
            `searchInput=${searchInput}`,
        ].join('&');

        let url = `/Archer3/GetFilerList?${params}`;
        await fetch(url)
            .then(result => result.json())
            .then(result => {

                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetFilerList',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let list = result.result;
                    setFilerList(list);
                    if (list.length > 0) {
                        props.setDefaultCpfId(list[0].cpfId);
                    }
                }
            })
    }

    const getSearchResults = (searchInput: string) => {
        setSearchInput(searchInput);
        if (searchInput.length>0) {
            setAuditorId(0);
            setStatus(5); //all
        }
        else {
            setAuditorId(0);
            setStatus(3); //outstanding
        }
    }

    const updateStatusId = (event: any) => {
        let statusId = event.target.value;
        setStatus(statusId);
    }

    const updateAuditorId = (auditorId: number) => {
        setAuditorId(auditorId);
        props.updateAuditorId(auditorId);
    }

    const getCurrentCpfId = (cpfId: number) => {
        context.setCurrentCpfId(cpfId);
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div className="filer-list">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    p: 0,
                    m: 0.5,
                    borderRadius: 1,
                }}
            >
                <Grid container>
                    <Stack direction="row" spacing={1}>
                        <AuditorsDropdown auditorId={auditorId} auditors={AUDITORS} updateAuditorId={updateAuditorId}></AuditorsDropdown>
                        <Box>
                            <FormControl sx={{ m: 1, maxWidth: 100, marginLeft: 0, marginRight: 0 }} size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedStatusId}
                                    label="Status"
                                    sx={{ fontSize: 14 }}
                                    onChange={updateStatusId}
                                >{
                                        LetterStatus.map(status =>
                                            <MenuItem key={status.statusId} value={status.statusId}>{status.description}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                    <Grid item xs={12} className="action-control">
                        <SearchSection placeHolder="Search Filers..." getSearchResults={getSearchResults} fontSize={14} searchInput={searchInput} ></SearchSection>
                    </Grid>
                </Grid>

                <Grid item xs={12} style={{ maxHeight: 800, minHeight: 800, minWidth: '100%', overflow: 'auto', marginTop: 15, paddingLeft:0 }}>
                    {
                        filerList && filerList.length>0 &&
                        filerList.map((filer: ArcherFiler) => (
                            <Item className={"is-clickable filer-list-item " + (filer.cpfId == context.currentCpfId ? 'filer-list-item-active' : '')} key={filer.cpfId} onClick={() => getCurrentCpfId(filer.cpfId)}>
                                <Stack direction="row" spacing={1}>
                                    {displayFilerStatusIcon(selectedStatusId)}
                                    <span>{filer.count > 0 ? `${filer.cpfId} ${filer.fullNameReverse} (${filer.count})` : `${filer.cpfId} ${filer.fullNameReverse}`}</span>
                                </Stack>
                            </Item>
                            ))
                    }
                </Grid>
            </Box>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    )
}

export default React.memo(FilerListSideBar);